import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { TextInput } from '../../../../../../components/textInput/TextInput';
import { CustomDialog } from '../../../../../../components/customDialog/CustomDialog';
import DataGridGenerico from '../../../../../../components/dataGrid/DataGridGenerico';
import useSearchGridData from '../../../../../../hooks/useSearchGridData';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';

import { Box, Grid, IconButton } from '@mui/material';
import { generateColumnsDefaultConfiguration } from '../../../../../../components/dataGrid/columnHelpers';
import { CustomButton } from '../../../../../../components/customButton/CustomButton';
import { Typography } from '@mui/material';

/** 
 * @Searcher
 * Componente que renderiza un campo de tipo buscador.
    @param {String} displayName String que aparecerá como label del campo
    @param {String} titleModal String que se mostrara como titulo del modal 
    @param {String} entityName String para representar el nombre de la entidad de la que se hará la query, por ejemplo `allClientes` para manejar las rows, por ejemplo `response.allClientes.items`.
    @param {Any} entityId Parametro que se usará como nombre del atributo identificador, principalmente para obtener el/los registros ya cargados, por ejemplo `idCliente`.
    @param {Any} entityIdValue Valor (idendificador) del atributo `entityId` que se a seleccionado en un componente `Search` que sea `multiple`:`false`.
    @param {Array} entityIdValues Array de valores (idendificadores) que se han seleccionado en un componente `Search` que sea `multiple`:`true`.
    @param {Array} columns Array de columnas para la grilla dentro del modal
    @param {Array} query GQL para hacer la query que traerá las grillas
    @param {Function} adapter Función para adaptar las rows obtenidas en la query
    @param {Function} getSearchParams Función Callback para retornar el criterio de busqueda de la grilla del modal en base al filtro de tipo string interno del modal.
    @param {Function} onRowClick Función que se ejecutará al momento de clickear una row
    @param {Function} getEntityValue Función Callback para cuando multiple: `false` que retornará el valor a mostrar en el campo. Ejemplo de valor a mostrar `0045343343-Facundo Perez`
    @param {boolean} multiple Valor booleano que determinará si el campo de busqueda acepta multiples valores.
    @param {Function} onSelect Función para cuando `multiple`: `true`, que se ejecutará al checkear una row.
    @param {Function} onDeleteRow Función para cuando `multiple`: `true`, que se ejecutará al borrar una row en la grilla de los valores SELECCIONADOS.
    @param {boolean} disabled Valos booleano que indicará si el campo está o no habilitado
 */
const Searcher = ({
    displayName,
    titleModal,
    entityName,
    entityId,
    entityIdValue,
    entityIdValues,
    columns,
    query,
    adapter,
    getSearchParams,
    onRowClick,
    getEntityValue,
    multiple,
    onSelect,
    onDeleteRow,
    disabled,
    dataGridTitle,
    dataGridButtonLabel,
    errorMessage
}) => {

    const [openModal, setOpenModal] = useState(false);
    const [entityValue, setEntityValue] = useState('');
    const [rowValues, setRowValues] = useState([]);
    const [orderBy, setOrderBy] = useState({
        field: entityId,
        sort: 'DESC'
    });

    useEffect(() => {
        if (!entityIdValue) {
            setEntityValue('');
        }
    }, [entityIdValue])

    const onSortModelChange = useCallback((model) => {
        if (model.length > 0) {
            const newOrderBy = {
                fieldName: model[0]?.field,
                order: model[0]?.sort?.toLowerCase(),
            };
            setOrderBy(newOrderBy);
        } else {
            setOrderBy(null);
        }
    }, []);

    const handleChangeEntityValue = (row) => {
        if (multiple) return;
        setEntityValue(getEntityValue(row));
    }

    const {
        rows,
        responseLoading,
        totalCount,
        page,
        pageSize,
        textFilter,
        onChangeFilter,
        onPageChange,
        onPageSizeChange
    } = useSearchGridData({
        entityId,
        entityName,
        entityIdValue,
        query,
        adapter,
        getSearchParams,
        handleChangeEntityValue,
        multiple,
        entityIdValues,
        setRowValues,
        orderBy
    });

    const memoSortModel = useMemo(() => (
        orderBy?.fieldName ? [{ field: orderBy.fieldName, sort: orderBy.order?.toLowerCase() }] : []
    ), [orderBy?.fieldName, orderBy?.order]);

    const deleteActionColumn = useMemo(() => (generateColumnsDefaultConfiguration([
        {
            field: 'action',
            headerName: '',
            align: 'center',
            renderCell: ({ row }) => {
                return (
                    <IconButton disabled={disabled} onClick={() => onDeleteRow(row.id, row[entityId])}>
                        <DeleteOutlinedIcon color={disabled ? 'disabled' : 'action'} />
                    </IconButton>
                )
            }
        }
    ]).at()), [onDeleteRow, disabled]);

    const handleOpenModal = () => setOpenModal(true);
    const handleCloseModal = () => setOpenModal(false);

    const handleRowClick = (params) => {
        onRowClick(params?.row);
        if (multiple) return;

        handleChangeEntityValue(params?.row);
        handleCloseModal();
    }

    return (
        <Grid container spacing={!!dataGridTitle ? 1 : 2}>
            {dataGridTitle && <Grid size={{ xs: 12 }}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <h3 style={{ margin: 0 }}>{dataGridTitle}</h3>
                    <CustomButton
                        label={dataGridButtonLabel}
                        variant='outlined'
                        onClick={handleOpenModal}
                        disabled={disabled}
                    />
                </Box>
            </Grid>}
            {!dataGridTitle && <Grid size={{ xs: 12, md: multiple ? 3 : 12 }}>
                <TextInput
                    placeholder={`Buscar ${displayName}`}
                    value={entityValue || entityIdValue}
                    label={displayName}
                    searchIcon
                    onClick={handleOpenModal}
                    disabled={disabled}
                    onClearButtonClick={(e) => {
                        if (disabled) return;
                        e.stopPropagation();
                        handleRowClick(null);
                    }}
                />
            </Grid>}
            {multiple && <Grid size={{ xs: 12 }}>
                <DataGridGenerico
                    hideFooterPagination={false}
                    loading={responseLoading}
                    toolbar={false}
                    columns={[deleteActionColumn, ...columns]}
                    rows={rowValues}
                    totalCount={entityIdValues.length}
                    onSortModelChange={onSortModelChange}
                    sortModel={memoSortModel}
                />
            </Grid>}
            <CustomDialog
                maxWidth='md'
                fullWidth
                onCancel={handleCloseModal}
                open={openModal}
                cancelText='Cerrar'
                title={titleModal || displayName}
            >
                {errorMessage && <Typography color='error' sx={{ fontSize: 10 }} > {errorMessage}</Typography>}
                <Grid container>
                    <Grid sx={{ my: 2.5 }} size={{ xs: 12, md: 6 }}>
                        <TextInput
                            placeholder='Filtrar'
                            value={textFilter}
                            searchIcon
                            onChange={(e) => onChangeFilter(e.target.value)}
                            onClearButtonClick={() => onChangeFilter('')}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <DataGridGenerico
                            hideFooterPagination={false}
                            loading={responseLoading}
                            toolbar={false}
                            columns={columns}
                            rows={errorMessage ? [] : rows}
                            currentPage={page}
                            pageSize={pageSize}
                            onPageChange={onPageChange}
                            onPageSizeChange={onPageSizeChange}
                            totalCount={totalCount}
                            onRowClick={handleRowClick}
                            onRowSelectionModelChange={onSelect}
                            checkboxSelection={multiple}
                            keepNonExistentRowsSelected={multiple}
                            rowSelectionModel={entityIdValues}
                            entityId={entityId}
                            onSortModelChange={onSortModelChange}
                            sortModel={memoSortModel}
                        />
                    </Grid>
                </Grid>
            </CustomDialog>
        </Grid >
    )
};

export default Searcher;